/**
 * Bibliothèque cloud : prises (éditeur) et projets studio (arrangement complet).
 *
 * Stockage : bucket privé `audio`, un dossier par utilisateur :
 *   <user_id>/takes/<id>.wav
 *   <user_id>/projects/<projet_id>/<fichier_id>.wav
 * Métadonnées : tables `takes` et `projects` (RLS : chacun ne voit que les siennes).
 * L'audio est sauvegardé en WAV mono 16-bit (le format de travail de l'app).
 */
import { supabase, uuid } from './supabase';
import { track } from './track';
import { encodeWav, toMono } from '../audio/wav';
import { decodeBlob } from '../audio/context';
import type { Track, Clip } from '../state/studio';

const BUCKET = 'audio';

function needClient() {
  if (!supabase) throw new Error('Comptes non configurés.');
  return supabase;
}

/* ------------------------------------------------------------------ prises */

export interface TakeRow {
  id: string;
  name: string;
  duration: number;
  bpm: number | null;
  key_name: string | null;
  path: string;
  created_at: string;
}

export async function saveTake(
  userId: string,
  name: string,
  buffer: AudioBuffer,
  meta?: { bpm?: number | null; keyName?: string | null },
): Promise<void> {
  const sb = needClient();
  const id = uuid();
  const path = `${userId}/takes/${id}.wav`;
  const wav = encodeWav(toMono(buffer), buffer.sampleRate);
  const up = await sb.storage.from(BUCKET).upload(path, wav, { contentType: 'audio/wav', upsert: true });
  if (up.error) throw new Error(`Envoi du fichier impossible : ${up.error.message}`);
  const ins = await sb.from('takes').insert({
    id, name, path,
    duration: buffer.duration,
    bpm: meta?.bpm ?? null,
    key_name: meta?.keyName ?? null,
  });
  if (ins.error) {
    await sb.storage.from(BUCKET).remove([path]).catch(() => undefined);
    throw new Error(`Sauvegarde impossible : ${ins.error.message}`);
  }
  track('sauvegarde-cloud', { type: 'prise', duree_sec: Math.round(buffer.duration) });
}

export async function listTakes(): Promise<TakeRow[]> {
  const sb = needClient();
  const { data, error } = await sb.from('takes').select('*').order('created_at', { ascending: false });
  if (error) throw new Error(`Lecture de la bibliothèque impossible : ${error.message}`);
  return (data ?? []) as TakeRow[];
}

export async function loadTake(row: TakeRow): Promise<AudioBuffer> {
  const sb = needClient();
  const { data, error } = await sb.storage.from(BUCKET).download(row.path);
  if (error || !data) throw new Error('Téléchargement de la prise impossible.');
  track('bibliotheque-chargement', { type: 'prise' });
  return decodeBlob(data);
}

export async function deleteTake(row: TakeRow): Promise<void> {
  const sb = needClient();
  await sb.storage.from(BUCKET).remove([row.path]);
  const { error } = await sb.from('takes').delete().eq('id', row.id);
  if (error) throw new Error(`Suppression impossible : ${error.message}`);
}

/* ----------------------------------------------------------------- projets */

export interface ProjectRow {
  id: string;
  name: string;
  bpm: number;
  beats_per_bar: number;
  data: ProjectData;
  created_at: string;
  updated_at: string;
}

interface SerClip { name: string; start: number; offset: number; duration: number; file: string }
interface SerTrack { name: string; volume: number; muted: boolean; solo: boolean; clips: SerClip[] }
export interface ProjectData { version: 1; tracks: SerTrack[] }

export interface ProjectSnapshot {
  bpm: number;
  beatsPerBar: number;
  tracks: Track[];
}

export async function saveProject(
  userId: string,
  name: string,
  snap: ProjectSnapshot,
  existingId?: string | null,
): Promise<string> {
  const sb = needClient();
  const id = existingId ?? uuid();
  const prefix = `${userId}/projects/${id}`;

  // chaque AudioBuffer unique n'est envoyé qu'une fois (clips dupliqués → même fichier)
  const files = new Map<AudioBuffer, string>();
  const tracks: SerTrack[] = [];
  for (const t of snap.tracks) {
    const clips: SerClip[] = [];
    for (const c of t.clips) {
      let file = files.get(c.buffer);
      if (!file) {
        file = `${uuid()}.wav`;
        files.set(c.buffer, file);
        const wav = encodeWav(toMono(c.buffer), c.buffer.sampleRate);
        const up = await sb.storage.from(BUCKET).upload(`${prefix}/${file}`, wav, { contentType: 'audio/wav', upsert: true });
        if (up.error) throw new Error(`Envoi de l'audio impossible : ${up.error.message}`);
      }
      clips.push({ name: c.name, start: c.start, offset: c.offset, duration: c.duration, file });
    }
    tracks.push({ name: t.name, volume: t.volume, muted: t.muted, solo: t.solo, clips });
  }

  const data: ProjectData = { version: 1, tracks };
  const { error } = await sb.from('projects').upsert({
    id, name, data,
    bpm: snap.bpm,
    beats_per_bar: snap.beatsPerBar,
    updated_at: new Date().toISOString(),
  });
  if (error) throw new Error(`Sauvegarde du projet impossible : ${error.message}`);
  track('sauvegarde-cloud', { type: 'projet', pistes: snap.tracks.length });
  return id;
}

export async function listProjects(): Promise<ProjectRow[]> {
  const sb = needClient();
  const { data, error } = await sb.from('projects').select('*').order('updated_at', { ascending: false });
  if (error) throw new Error(`Lecture des projets impossible : ${error.message}`);
  return (data ?? []) as ProjectRow[];
}

export async function loadProject(
  userId: string,
  row: ProjectRow,
  makeIds: () => string,
): Promise<ProjectSnapshot> {
  const sb = needClient();
  const prefix = `${userId}/projects/${row.id}`;
  // télécharge chaque fichier une seule fois
  const buffers = new Map<string, AudioBuffer>();
  const fileNames = new Set<string>();
  for (const t of row.data.tracks) for (const c of t.clips) fileNames.add(c.file);
  for (const f of fileNames) {
    const { data, error } = await sb.storage.from(BUCKET).download(`${prefix}/${f}`);
    if (error || !data) throw new Error(`Un fichier audio du projet est introuvable (${f}).`);
    buffers.set(f, await decodeBlob(data));
  }
  track('bibliotheque-chargement', { type: 'projet' });
  const tracks: Track[] = row.data.tracks.map((t) => ({
    id: makeIds(),
    name: t.name, volume: t.volume, muted: t.muted, solo: t.solo,
    clips: t.clips.map((c): Clip => ({
      id: makeIds(),
      name: c.name, buffer: buffers.get(c.file)!,
      start: c.start, offset: c.offset, duration: c.duration,
    })),
  }));
  return { bpm: row.bpm, beatsPerBar: row.beats_per_bar, tracks };
}

export async function deleteProject(userId: string, row: ProjectRow): Promise<void> {
  const sb = needClient();
  const prefix = `${userId}/projects/${row.id}`;
  const { data: objs } = await sb.storage.from(BUCKET).list(prefix);
  if (objs?.length) {
    await sb.storage.from(BUCKET).remove(objs.map((o) => `${prefix}/${o.name}`));
  }
  const { error } = await sb.from('projects').delete().eq('id', row.id);
  if (error) throw new Error(`Suppression du projet impossible : ${error.message}`);
}
